import React from 'react'
import PropTypes from 'prop-types'

import {
  StyledBreadcrumbs,
  StyledBreadcrumbsCurrent,
  StyledBreadcrumbsLink,
  StyledBreadcrumbsSeparator,
  StyledContainer,
} from './Breadcrumbs.styled'

import RoutingEnum from '../../routes/appRoutes'
import AppRoutes from '../../routes/appRoutes'
import useFormatBreadcrumb from '../../hooks/useFormatBreadcrumb'
import {
  CAREERS_SLUG,
  HOME_LABEL,
  INVESTORS_SLUG,
  JOBS_SEARCH_SLUG,
  LOCATIONS_SLUG,
  NEWS_SLUG,
  PATHNAME_SPACER,
  PRODUCTS_SLUG,
  STORY_SLUG,
  TYPES_ENUM,
} from '../../config/BreadCrumbsConfig'
import { deepSearchByKey } from '../../utils/DeepSearchByKey'
import { useNavigationQuery } from '../../graphql/useNavigationQuery'

const Breadcrumbs = ({ pathname, slug, title, type }) => {
  const data = useNavigationQuery()

  const companyNavigation =
    data.contentfulComponentNavigationFooter.companyNavigation
  const profilNavigation =
    data.contentfulComponentNavigationFooter.profilNavigation

  const navigations = [...companyNavigation, ...profilNavigation]

  const [formattedPathname] = useFormatBreadcrumb(pathname)

  const flattenedNavItems = deepSearchByKey(navigations, 'name')

  const getParentSlug = (type) => {
    switch (type) {
      case TYPES_ENUM.article:
        return NEWS_SLUG
      case TYPES_ENUM.location:
        return LOCATIONS_SLUG
      case TYPES_ENUM.story:
        return STORY_SLUG
      default:
        return JOBS_SEARCH_SLUG
    }
  }

  const breadcrumbsGenerator = () => {
    // filter to handle "/" (Homepage) at the beginning of pathname
    // and at the end of pathname if there is one
    // Reversed array in order to fall back to the first parent and get the URL from navigation Contentful
    const arrOfPaths = formattedPathname
      .split(PATHNAME_SPACER)
      .filter((path) => path !== '')
      .reverse()

    // Retrieve current path to get lastItem
    const currentPath = pathname.substring(1).replace(/^\/|\/$/g, '')

    let lastItem = flattenedNavItems.find(
      (e) =>
        e.pageReference?.slug === currentPath ||
        (e.slug !== null && e.slug === currentPath)
    )
    let lastParentItem = flattenedNavItems.find((e) =>
      e.subNavigationItems?.some((c) => c.id === lastItem?.id)
    )

    // Specific for Job search
    if (
      pathname === AppRoutes.jobSearch ||
      pathname === AppRoutes.jobSearchWithTrailing
    ) {
      const parentSlug = CAREERS_SLUG
      lastItem = {
        name: title,
        slug: pathname,
      }
      lastParentItem = flattenedNavItems.find(
        (e) =>
          e.pageReference?.slug === parentSlug ||
          (e.slug !== null && e.slug === parentSlug)
      )
    }

    if (pathname.split('/')[1] === 'products') {
      const parentSlug = PRODUCTS_SLUG
      lastItem = {
        name: title,
        slug: pathname,
      }
      lastParentItem = flattenedNavItems.find(
        (e) =>
          e.pageReference?.slug === parentSlug ||
          (e.slug !== null && e.slug === parentSlug)
      )
    }

    // Specific for Job search
    if (pathname === AppRoutes.secFilings) {
      const parentSlug = INVESTORS_SLUG
      lastItem = {
        name: title,
        slug: pathname,
      }
      lastParentItem = flattenedNavItems.find(
        (e) =>
          e.pageReference?.slug === parentSlug ||
          (e.slug !== null && e.slug === parentSlug)
      )
    }

    // Specifig for article / Location page / job detail page / Story page
    if (
      type === TYPES_ENUM.article ||
      type === TYPES_ENUM.location ||
      type === TYPES_ENUM.job ||
      type === TYPES_ENUM.story
    ) {
      const parentSlug = getParentSlug(type)
      lastItem = {
        name: title,
        slug,
      }
      lastParentItem = flattenedNavItems.find(
        (e) =>
          e.pageReference?.slug === parentSlug ||
          (e.slug !== null && e.slug === parentSlug)
      )
    }

    if (
      slug === AppRoutes.contact ||
      slug === AppRoutes.notFound ||
      slug === AppRoutes.cookiePolicy
    ) {
      lastItem = {
        name: title,
        slug,
      }
      lastParentItem = []
    }

    if (slug === undefined && lastItem === undefined && currentPath) {
      const currentPathArr = currentPath.split('/')
      const parentSlug = currentPathArr[currentPathArr.length - 2]
      lastItem = {
        name: title,
        slug: pathname,
      }
      lastParentItem =
        flattenedNavItems.find(
          (e) =>
            e.pageReference?.slug === parentSlug ||
            (e.slug !== null && e.slug === parentSlug)
        ) || []
    }

    let currentItem = lastItem
    let parentItem = lastParentItem

    const breadCrumbsItem = []

    if (lastItem === null) return null

    arrOfPaths.forEach((e, i) => {
      if (i !== 0 && i <= arrOfPaths.length - 1) {
        currentItem = parentItem
        parentItem = flattenedNavItems.find((e) =>
          e.subNavigationItems?.some((c) => c.id === currentItem?.id)
        )

        breadCrumbsItem.push(
          <StyledBreadcrumbsLink
            label={currentItem?.name || currentItem?.pageReference?.name}
            to={`/${currentItem?.pageReference?.slug || currentItem?.slug}`}
          />
        )
      }
    })

    breadCrumbsItem.reverse()
    arrOfPaths.reverse()

    return arrOfPaths.map((path, index) => {
      return (
        <StyledContainer key={`${path}-${index}`}>
          <StyledBreadcrumbsSeparator />
          {arrOfPaths.length - 1 === index ? (
            <StyledBreadcrumbsCurrent>
              {lastItem?.name || lastItem?.pageReference?.name}
            </StyledBreadcrumbsCurrent>
          ) : (
            breadCrumbsItem[index]
          )}
        </StyledContainer>
      )
    })
  }
  const breadcrumbs = breadcrumbsGenerator()

  return (
    <nav>
      <StyledBreadcrumbs>
        <li>
          <StyledBreadcrumbsLink
            label={HOME_LABEL}
            to={RoutingEnum.home}
            isHome
          />
        </li>
        {breadcrumbs}
      </StyledBreadcrumbs>
    </nav>
  )
}

Breadcrumbs.propTypes = {
  pathname: PropTypes.string.isRequired,
}

export default Breadcrumbs
